import { defineStore } from 'pinia'
import { axiosClient } from '@/utils/axios-client.ts'
import { markRaw, ref, shallowRef } from 'vue'
import type { Page } from '@/types/page.ts'
import {
  type ChangelogEntry,
  type IopFormMessage,
  type IopFormMessageError,
} from '@/types/iop-form.ts'
import { Client, type IFrame, type IMessage } from '@stomp/stompjs'

export const useIopFormStore = defineStore('iopFormStore', () => {
  const forms = shallowRef<IopFormMessage[]>([])
  const failures = ref<IopFormMessageError[]>([])

  const stompClient = ref<Client | null>(null)
  const stompConnected = ref(false)

  async function fetchAllIopForms() {
    const response = await axiosClient.request<Page<IopFormMessage>>({
      url: '/iop/administration/forms/all?size=25',
      method: 'GET',
    })

    forms.value = response.data.content

    return response.data
  }

  async function fetchAllIopFailures() {
    const response = await axiosClient.request<Page<IopFormMessageError>>({
      url: '/iop/administration/failures/all?size=25',
      method: 'GET',
    })

    failures.value = response.data.content

    return response.data
  }

  async function fetchReadableChangelog(iopFormUuid: string): Promise<ChangelogEntry[]> {
    const response = await axiosClient.request<ChangelogEntry[]>({
      url: '/iop/administration/readable-changelog/' + iopFormUuid,
      method: 'GET',
    })
    const data: ChangelogEntry[] = response.data
    data.sort((change1, change2) => change1.sourceFormVersion - change2.sourceFormVersion)
    return data
  }

  function connectWS() {
    if (stompClient.value?.active) {
      return
    }

    const client = markRaw(
      new Client({
        connectHeaders: {
          Authorization: `Bearer ${import.meta.env['VITE_IOP_WS_KEY']}`,
        },
        brokerURL: `ws://${import.meta.env['VITE_IOP_WS_URL']}/iopws?api_key=${import.meta.env['VITE_IOP_WS_KEY']}`,
        reconnectDelay: 5000,

        onConnect: () => {
          stompConnected.value = true

          client.subscribe('/iop/formEnvelop', (message: IMessage) => {
            handleFormEnvelop(message)
          })
          client.subscribe('/iop/formFailure', (message: IMessage) => {
            handleFailureForm(message)
          })
        },

        onDisconnect: () => {
          stompConnected.value = false
        },

        onStompError: (frame: IFrame) => {
          console.error('STOMP error:', frame.headers['message'])
          console.error('Details:', frame.body)
        },

        onWebSocketClose: () => {
          stompConnected.value = false
        },

        onWebSocketError: (event: unknown) => {
          console.error('WebSocket error:', event)
        },
      })
    )

    stompClient.value = client
    client.activate()
  }

  function disconnectWS() {
    if (!stompClient.value) {
      return
    }

    stompClient.value.deactivate()
    stompClient.value = null
    stompConnected.value = false
  }

  function handleFormEnvelop(message: IMessage) {
    console.log('Received message:', message.body)
    const data = JSON.parse(message.body) as IopFormMessage
    console.log('Received message:', data)
    console.log('new form list: ', forms.value)
    forms.value = [data, ...forms.value]
  }

  function handleFailureForm(message: IMessage) {
    console.log('Received message:', message.body)
    const data = JSON.parse(message.body) as IopFormMessageError
    failures.value = [data, ...failures.value]
  }

  return {
    forms,
    failures,
    fetchAllIopForms,
    fetchAllIopFailures,
    fetchReadableChangelog,
    connectWS,
    disconnectWS,
  }
})
